import {
	ButtonBase,
	Chip,
	Collapse,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { ChevronDown } from "lucide-react";
import { type FC, useState } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailRow } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailRow";
import type { ToolHistoryItem } from "web_myplann/Portfolio/Components/Redesign/Detail/FundDetail/useToolHistoryItems";

const FundResources = Resources.PortfolioDetail.ToolHistory.Fund;

type Props = {
	item: ToolHistoryItem;
	isLast: boolean;
};

export const ToolHistoryRow: FC<Props> = ({ item, isLast }) => {
	const theme = useTheme();
	const { t } = useResource();
	const [expanded, setExpanded] = useState(false);

	return (
		<Stack
			sx={{
				borderBottom: isLast
					? "none"
					: theme.colors.border.solid(theme.palette.grey[100]),
			}}
		>
			<Stack
				component={ButtonBase}
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				gap={theme.spacingTokens.md}
				px={theme.spacingTokens.lg}
				py={theme.spacingTokens.md}
				onClick={() => setExpanded((prev) => !prev)}
				width="100%"
				textAlign="left"
			>
				<Stack
					direction="row"
					alignItems="center"
					gap={theme.spacingTokens.sm}
					minWidth={0}
				>
					{item.directionLabel && (
						<Chip
							variant="tag"
							color={item.isBuy ? "success" : "error"}
							label={item.directionLabel}
						/>
					)}
					<Typography
						variant="caption"
						color={theme.palette.grey[500]}
						tabular
						noWrap
					>
						{item.startDate}
					</Typography>
				</Stack>
				<Stack
					direction="row"
					alignItems="center"
					gap={theme.spacingTokens.sm}
					flexShrink={0}
				>
					<Typography variant="bodyMedium" tabular>
						{item.amount}
					</Typography>
					<ChevronDown
						size={theme.iconSize.sm}
						color={theme.palette.grey[400]}
						style={{
							flexShrink: 0,
							transform: expanded ? "rotate(180deg)" : "none",
							transition: "transform 200ms",
						}}
					/>
				</Stack>
			</Stack>

			<Collapse in={expanded} timeout="auto" unmountOnExit>
				<Stack pb={theme.spacingTokens.sm}>
					<DetailRow
						label={t(FundResources.type)}
						value={item.directionLabel}
					/>
					<DetailRow
						label={t(FundResources.startDate)}
						value={item.startDate}
					/>
					<DetailRow label={t(FundResources.endDate)} value={item.endDate} />
					<DetailRow label={t(FundResources.amount)} value={item.amount} />
					<DetailRow
						label={t(FundResources.frequency)}
						value={item.frequency}
					/>
					<DetailRow
						label={t(FundResources.transactionType)}
						value={item.transactionLabel}
					/>
				</Stack>
			</Collapse>
		</Stack>
	);
};
